import { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { getBaseUrl, getUsersAdmin } from '../../utils/api';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpIcon from '@mui/icons-material/Help';
import ListIcon from '@mui/icons-material/List';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import Leaderboard from './leaderboard';
import Scorecard from './scorecard';
import Faq from './faq';
import EditProblems from './edit-problems';
import Users from './users';
import Profile from './profile';
import { Edit } from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';

const darkTheme = createTheme({
  palette: {
    mode: 'dark'
  }
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function LinkTab(props) {
  return (
    <Tab
      component="a"
      onClick={(event) => {
        event.preventDefault();
      }}
      {...props}
    />
  );
}

const Contest = () => {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const [accessToken, setAccessToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoaded, setIsAdminLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  // Map pathname to tab index
  const getTabFromPath = (pathname: string) => {
    if (pathname.includes('/leaderboard')) return 1;
    if (pathname.includes('/faq')) return 2;
    if (pathname.includes('/profile')) return 3;
    if (pathname.includes('/edit')) return 4;
    if (pathname.includes('/users')) return 5;
    return 0; // default to scorecard
  };

  const [value, setValue] = useState(0);

  // Update tab when URL changes - NEVER set to admin tabs unless confirmed admin
  useEffect(() => {
    const newValue = getTabFromPath(location.pathname);
    
    // For admin tabs, only set if user is confirmed admin
    if (newValue === 4 || newValue === 5) {
      if (isAdminLoaded && isAdmin) {
        setValue(newValue);
      } else if (isAdminLoaded && !isAdmin) {
        // Not admin, redirect away
        setValue(3);
        navigate('/contest/profile', { replace: true });
      }
      // If not loaded yet, do nothing - keep current value
      return;
    }
    
    // Safe to set non-admin tab values
    setValue(newValue);
  }, [location.pathname, isAdmin, isAdminLoaded, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (accessToken) {
      getUsersAdmin(accessToken).then((res) => { 
        setIsAdmin(res);
        setIsAdminLoaded(true);
      });
    }
  }, [accessToken]);

  // Prevent setting admin tab values until AFTER admin tabs are rendered
  useLayoutEffect(() => {
    if ((value === 4 || value === 5) && !isAdmin) {
      setValue(0);
    }
  }, [value, isAdmin]);

  const handleTabChange = (_, newValue) => {
    const authTabIndex = 3;

    if (newValue !== authTabIndex) {
      setValue(newValue);
      
      // Update URL based on tab
      const paths = ['/contest', '/contest/leaderboard', '/contest/faq', '/contest/profile', '/contest/edit', '/contest/users'];
      if (newValue >= 0 && newValue < paths.length) {
        navigate(paths[newValue]);
      }
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    // Don't set value directly - let URL effect handle it after admin check
    navigate('/contest/edit');
    handleMenuClose();
  };

  const handleUsersClick = () => {
    // Don't set value directly - let URL effect handle it after admin check
    navigate('/contest/users');
    handleMenuClose();
  };

  const handleProfileClick = () => {
    setValue(3);
    navigate('/contest/profile');
    handleMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    logout({ logoutParams: { returnTo: getBaseUrl() } });
  };

  // Compute safe value - never return admin tabs unless confirmed admin
  const safeValue = useMemo(() => {
    // Max valid value depends on whether admin tabs are rendered
    const maxValid = isAdmin ? 5 : 3;
    if (value > maxValid) {
      return false; // MUI accepts false as "no selection"
    }
    return value;
  }, [value, isAdmin]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Paper sx={{ maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', minHeight: '100vh' }}>
        <Tabs
          variant="fullWidth"
          value={safeValue}
          onChange={handleTabChange}
          slotProps={{ indicator: { style: isAdmin ? {} : { display: 'none' } } }}
        >
          <LinkTab icon={<ListIcon />} href="scorecard" label="Tick" />
          <LinkTab icon={<FitnessCenterIcon />} href="leaderboard" label="Top" />
          <LinkTab icon={<HelpIcon />} href="faq" label="FAQ" />
          
          {!isAuthenticated ? (
            <Tab 
                icon={<AccountCircleIcon />} 
                label="Sign in"
                onClick={() => loginWithRedirect({ appState: { returnTo: location.pathname } })} 
            />
          ) : (
            <Tab 
                icon={<MoreVertIcon />} 
                label="More"
                onClick={handleMenuOpen} 
            />
          )}
          
          {/* Hidden tabs for admin pages - needed for MUI validation */}
          <Tab style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }} />
          <Tab style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }} />
        </Tabs>

        {isAuthenticated && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            {isAdmin && <Divider />}
            {isAdmin && (
              <MenuItem onClick={handleEditClick}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit problems</ListItemText>
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={handleUsersClick}>
                <ListItemIcon>
                  <PeopleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Users</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </MenuItem>
          </Menu>
        )}

        <TabPanel value={safeValue} index={0}>
          <Scorecard />
        </TabPanel>
        <TabPanel value={safeValue} index={1}>
          <Leaderboard />
        </TabPanel>
        <TabPanel value={safeValue} index={2}>
          <Faq />
        </TabPanel>
        
        <TabPanel value={safeValue} index={3}>
            <Profile />
        </TabPanel>
        
        <TabPanel value={safeValue} index={4}>
            {isAdmin ? <EditProblems /> : <div>Access Denied</div>}
        </TabPanel>
        
        <TabPanel value={safeValue} index={5}>
            {isAdmin ? <Users /> : <div>Access Denied</div>}
        </TabPanel>
      </Paper>
    </ThemeProvider>
  );
}

export default Contest;